import React, { Fragment, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCheckIfUserIsInUS, useResizeObserver, usePrevious } from '../../_shared/react-hooks';
import { ProductNode } from '../types';
import { ServerPlpData, InlineContentEntry } from 'src/@types/window-extended';
import { useLazyQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { SwatchUI } from './SwatchUI';
import { CustomPlusIcon } from './SmallUIComponents';
import {
  fireGA4ClickOnProductFromListEvent,
  fireGA4viewPLPproductsEvent,
  pushEventToGTMDataLayer,
} from '../../../utils/gtm-data-layer';
import QuickAddPanel from '../quick-add/QuickAddPanel';
import { toLocalCurrencyShowEstimate } from '../../../utils/currency';
import { observer } from 'mobx-react-lite';
import {
  checkIfCustomer,
  checkIfItemCanBeSoldOut,
  getOnlineSalePricing,
  getUrlWithoutParams,
  heapTrack,
  IS_NATIVE_APP_CLIENT,
  isTagPresent,
} from 'src/scripts/utils/helpers';
import { E_GIFT_CARD_PRODUCT_TYPE, PREORDER_TAG, PRODUCT_TILE_FALLBACK_IMG } from '../../../utils/constants';
import { getDiscountBadgeCopy, productDataGenerator } from '../../_shared/productTileDataComputation';
import ErrorBoundary from 'src/scripts/utils/react/ErrorBoundary';
import { PlpTileHoverableImage } from './PlpTileHoverableImage';
import { useMediaQuery } from '@material-ui/core';
import QuickAddMobile from '../quick-add/QuickAddMobile';
import { notNil } from '../../../utils/typescriptUtils';
import {
  determineATCandWishlistEligibility,
  determineSwatchEligibility,
  getSoldOutCopy,
  hasCannotBeShippedOutsideUSTag,
  isComingSoon,
  productBadgeText,
} from 'src/scripts/utils/plp-pdp-shared';
import { Badge } from './Badge';
import { WishlistIconPLP } from '../../_shared/wishlist/WishlistIconPLP';
import { getIdFromShopifyGraphQLId } from '../../../utils/shopify';
import { wishlistState } from '../../_shared/wishlist/wishlistState';
import { extractPurchaseLimitTagData } from '../../_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import { IQuickFitSizeSelectorData } from '../../../sections/header/Cart/components/CartImpulseBuy/QuickAddVariantSelector';
import { PlpTileScrollableImages } from './PlpTileScrollableImages';
import { ShippingCountryState } from '../../../utils/shipping';
import {
  PlpProductsExtendedQuery,
  PlpProductsExtendedQueryVariables,
} from '../quick-add/GetExtendedProduct.shopify.generated';
import { prependLocaleToPath } from '../../../utils/localization';

const PLPBuilderContent = lazy(
  () =>
    import(
      /* webpackChunkName: "PLPBuilderContent" */
      './PLPBuilderContent'
    )
);

const extendedProductQuery = loader('../quick-add/GetExtendedProduct.shopify.graphql');

export const ProductCards = ({
  products,
  filteredProducts,
  collectionHandle,
  collectionShopifyId,
  hasAFilterSelected,
  source,
}: {
  products: ProductNode[];
  filteredProducts: ProductNode[];
  collectionHandle: string | undefined;
  collectionShopifyId: string;
  hasAFilterSelected: boolean;
  source: string;
}) => {
  const productIdsToShow = useMemo(() => filteredProducts.map((item) => item.id), [filteredProducts]);

  const prevProducts = usePrevious(products);

  useEffect(() => {
    const _prevProducts = prevProducts || [];
    if (products.length > _prevProducts.length) {
      const previouslySentIDs = _prevProducts.map((product) => product.id);
      const newlyFetchedProducts =
        _prevProducts.length > 0
          ? products
              .filter((product) => {
                return !previouslySentIDs.includes(product.id) ? product : null;
              })
              .filter(notNil)
          : products;
      const startIndex = _prevProducts.length;
      fireGA4viewPLPproductsEvent(newlyFetchedProducts, collectionHandle, startIndex, collectionShopifyId);
    }
  }, [products, prevProducts, collectionHandle, collectionShopifyId]);

  const isCustomer = useMemo(() => {
    return checkIfCustomer();
  }, []);

  const isWishlist = useMemo(() => {
    const url = window.location.href;
    return url.includes('view=wishlist');
  }, []);

  return (
    <Fragment>
      {filteredProducts.map((product, index) => {
        return (
          <ErrorBoundary key={product.id}>
            <PlpTile
              collectionHandle={collectionHandle}
              collectionShopifyId={collectionShopifyId}
              productData={product}
              key={product.id}
              htmlProps={{ hidden: !productIdsToShow.includes(product.id) }}
              index={index}
              hasAFilterSelected={hasAFilterSelected}
              isCustomer={isCustomer}
              isWishlist={isWishlist}
              source={source}
              quickFitSizeSelectorText={product?.quickFitSizeSelectorText}
            />
          </ErrorBoundary>
        );
      })}
    </Fragment>
  );
};

//@ts-ignore
const serverPlpData: ServerPlpData | undefined = window.server_plp_data; // alias for types

let PlpTile = ({
  productData,
  htmlProps = {},
  collectionHandle,
  collectionShopifyId,
  index,
  hasAFilterSelected,
  isCustomer,
  isWishlist,
  source,
  quickFitSizeSelectorText,
  shouldHideBuilderContent,
  merchant,
}: {
  productData: ProductNode;
  htmlProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
  collectionHandle: string | undefined;
  collectionShopifyId: string;
  index: number;
  hasAFilterSelected: boolean;
  isCustomer: boolean;
  isWishlist: boolean;
  source: string;
  quickFitSizeSelectorText?: IQuickFitSizeSelectorData;
  shouldHideBuilderContent?: boolean;
  merchant?: string;
}) => {
  const isDesktop = useMediaQuery('(min-width: 1200px)', { noSsr: true });
  const isMobileAndTablet = !isDesktop;
  const areTheseSets = productData.vendor === 'Alo Yoga Set' || productData.productType === 'sets'; // ? [REVISIT] How suffice this check?

  const generatedProductData = useMemo(() => productDataGenerator(productData, { collectionHandle }), [
    collectionHandle,
    productData,
  ]);

  const {
    title,
    needsUrgencyBadge,
    href,
    primaryImage,
    secondaryImage,
    baseTitle,
    colorName,
    handle,
    isCurveCollection,
    tags,
    availableForSale,
  } = generatedProductData.computedData;

  const hasInternationalRestrictions = determineATCandWishlistEligibility(tags);
  let availableColors = generatedProductData.availableColors;
  const hideSingleSwatch = useMemo(() => determineSwatchEligibility(productData.productType), [productData]);

  const productId = generatedProductData.computedData.id || getIdFromShopifyGraphQLId(productData.id) || '';
  const soldOutCopy = getSoldOutCopy(tags, areTheseSets);
  const _isComingSoon = isComingSoon(tags);
  const isSoldOut = checkIfItemCanBeSoldOut(tags)
    ? isWishlist && productData.totalInventory === 0 && !areTheseSets && !_isComingSoon
    : false;
  const selectedBadge = isSoldOut ? soldOutCopy : productBadgeText(tags, availableForSale);
  const isPreorder = isTagPresent(tags, PREORDER_TAG);
  const [productBadgeCopy, setProductBadgeCopy] = useState(selectedBadge);

  const tooltipMessage =
    !areTheseSets && !isPreorder && needsUrgencyBadge ? 'almost gone' : isSoldOut ? soldOutCopy : null;
  // make the image it's own component & then this woulds be unecessary; solve w/ lazy sizes
  const { activeSwatchIndex } = generatedProductData;

  // ! [REVISIT] performance; resizeObserver on parent?; any oberver > polling w/ debounce
  const [ref, dimensions] = useResizeObserver();

  // ? [REVISIT] use css variable
  const swatchPlusGutterWidth = 35;
  const { width: swatchWrapperWidth } = dimensions;
  const swatchesThatWillFit = swatchWrapperWidth ? Math.floor(swatchWrapperWidth / swatchPlusGutterWidth) : 4; // default to 4??

  const swatchesWontFit = availableColors.length > swatchesThatWillFit;

  // determine user location & product international shipping availability to hide quick add if necessary
  const cannotBeShippedOutsideUS = hasCannotBeShippedOutsideUSTag(tags);
  const userIsInUS = useCheckIfUserIsInUS();
  const productNotAvailableForNonUScustomer = cannotBeShippedOutsideUS && !userIsInUS;

  // Quick Add
  const isAppExclusive = isTagPresent(tags, 'policy:inappexclusive'); // [REVISIT] dupe
  const shouldRenderQuickAdd =
    !isPreorder &&
    productData.productType.toLowerCase() !== E_GIFT_CARD_PRODUCT_TYPE &&
    !productNotAvailableForNonUScustomer &&
    !_isComingSoon &&
    !['Alo Yoga Set', 'Alo Yoga Simple'].includes(productData.vendor) &&
    (isDesktop || isMobileAndTablet) &&
    (!isAppExclusive || IS_NATIVE_APP_CLIENT) &&
    !areTheseSets &&
    !isSoldOut;
  const supportsHover = window.matchMedia('(hover: hover)');

  const getQuickAddDataOnHover = () => {
    if (supportsHover.matches && shouldRenderQuickAdd) {
      getExtendedProductData();
    }
  };

  const primaryImageUrl = primaryImage ? getlargeImageWidth(primaryImage) : null;
  const secondaryImageUrl = secondaryImage ? getlargeImageWidth(secondaryImage) : null;

  const [loadProductData, { error: quickAddQueryError, data: extendedData }] = useLazyQuery<
    PlpProductsExtendedQuery,
    PlpProductsExtendedQueryVariables
  >(extendedProductQuery, {
    variables: { handle },
  });
  const getExtendedProductData = useCallback(() => {
    loadProductData();
  }, [loadProductData]);

  const { price, compareAtPrice, priceRangeString, maxPrice } = getPricing(extendedData, productData);

  // passed down to be used in swipeable mobile images, turns extended image urls into actual images
  const fetchAdditionalImages = async (primaryImageUrl: string, secondaryImageUrl: string | null) => {
    return (extendedData?.productByHandle?.images.edges.slice(0, 6) ?? [])
      .filter(notNil)
      .map((edge) => getlargeImageWidth(edge.node.originalSrc))
      .filter(
        (imageUrl: string) =>
          getUrlWithoutParams(imageUrl) !== getUrlWithoutParams(primaryImageUrl) &&
          getUrlWithoutParams(imageUrl) !== getUrlWithoutParams(secondaryImageUrl)
      );
  };

  // for triggering mobile PLP tile animation
  const [firstTileVisible, setFirstTileVisible] = useState(false);

  const isScrollableImagesEnabled = !isDesktop && !isWishlist && collectionHandle !== 'search-page-drawer';

  if (quickAddQueryError) {
    console.log('Error querying the Quick Add data: ', quickAddQueryError);
  }

  const productCard = useRef<HTMLAnchorElement | null>(null);
  const initProductCardIntersectionObserver = (observer: IntersectionObserver) => {
    const _observe = () => {
      if (productCard.current) observer.observe(productCard.current);
    };

    try {
      _observe();
    } catch (err) {
      console.log('Failed to init Product Card Intersection Observer, err: ', err);
    }
  };

  useEffect(() => {
    if (isDesktop || (!shouldRenderQuickAdd && !isScrollableImagesEnabled)) {
      return;
    }
    const getProductDataConfig = {
      root: null,
      threshold: 0.2,
    };

    const callBack: IntersectionObserverCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > getProductDataConfig.threshold) {
          getExtendedProductData();
          observer.disconnect();
        }
      });
    };
    const getProductDataObserver = new IntersectionObserver(callBack, getProductDataConfig);
    initProductCardIntersectionObserver(getProductDataObserver);

    // intersection observer for triggering mobile PLP tile animation
    const firstTileVisibilityConfig = {
      root: null,
      threshold: 1,
    };
    const setFirstTileVisibleObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio === 1) {
          setFirstTileVisible(true);
          observer.disconnect();
        }
      });
    }, firstTileVisibilityConfig);
    initProductCardIntersectionObserver(setFirstTileVisibleObserver);

    return () => {
      getProductDataObserver.disconnect();
      setFirstTileVisibleObserver.disconnect();
    };
  }, [shouldRenderQuickAdd, isDesktop, getExtendedProductData, isScrollableImagesEnabled]);

  useEffect(() => {
    setProductBadgeCopy(selectedBadge);
  }, [selectedBadge]);

  // this is passed down into all the quick add components
  const purchaseLimitTagData = extractPurchaseLimitTagData(extendedData?.productByHandle?.tags);

  //#region => For PLPBuilderContent
  const inlineContentEntries =
    (serverPlpData && serverPlpData.inline_content_entries && serverPlpData.inline_content_entries) || [];

  const shouldRenderPLPBuilderContent =
    inlineContentEntries.some((entry) => entry.product === generatedProductData.computedData.handle) &&
    !hasAFilterSelected &&
    !shouldHideBuilderContent;

  let inlineContentEntry: InlineContentEntry | null = null;

  if (shouldRenderPLPBuilderContent) {
    const foundInlineContentEntry = inlineContentEntries.find(
      (entry) => entry.product === generatedProductData.computedData.handle
    );

    if (foundInlineContentEntry) {
      inlineContentEntry = foundInlineContentEntry;
    }
  }
  //#endregion

  const productURL = isCurveCollection ? `${href}?curve=1` : href;

  const ga4options = {
    index: index + 1,
    collectionHandle: collectionHandle || '',
    collectionShopifyId,
  };
  const discountBadgeCopy = compareAtPrice ? getDiscountBadgeCopy(compareAtPrice, price, maxPrice) : null;

  const badgeCopy = useMemo(() => {
    if (discountBadgeCopy && discountBadgeCopy.length > 1) {
      return discountBadgeCopy;
    } else if (productBadgeCopy && productBadgeCopy.length > 1) {
      return productBadgeCopy;
    } else return null;
  }, [discountBadgeCopy, productBadgeCopy]);

  const wishListGA4ProductData = {
    source: source && source.toLowerCase() === 'search-page' ? 'search results' : source.toLowerCase(),
    variantTitle: colorName,
    id: productId,
    title: title,
    productType: generatedProductData.computedData.productType,
    price: price,
    displayPrice: price,
    discount: compareAtPrice && price ? compareAtPrice - price : undefined,
    index: index + 1,
    collectionHandle: collectionHandle,
    collectionShopifyId,
  };

  const clickOnProductFromListGA4EventData = {
    productType: generatedProductData.computedData.productType,
    productTitle: title,
    productId,
    productVariant: colorName,
    index,
    collectionShopifyId,
    collectionName: collectionHandle,
    merchant,
  };

  // ! [REVISIT] accessibility, tabindex, etc.
  return (
    <>
      <div {...htmlProps} className={`PlpTile ${htmlProps.className || ''}`} ref={ref}>
        <div className={`position-relative`} onMouseEnter={getQuickAddDataOnHover}>
          {!hasInternationalRestrictions ? (
            <WishlistIconPLP source={source} isCustomer={isCustomer} productData={wishListGA4ProductData} />
          ) : null}
          <a
            ref={productCard}
            href={productURL}
            className={`aspect-ratio-3x2-wrapper image-wrapper
              ${isSoldOut ? 'wishlist-product-soldout' : ''}`}
            tabIndex={-1}
            onClick={() => {
              try {
                fireGA4ClickOnProductFromListEvent(clickOnProductFromListGA4EventData);
                if (isWishlist) {
                  heapTrack('Web - Click - Wishlist Product', {
                    isSignedIn: isCustomer,
                    productId,
                    'Wishlist Items Count': wishlistState.productIds.length,
                  });
                }
              } catch (error) {
                window.location.href = prependLocaleToPath(productURL);
                console.log(error);
              }
            }}
          >
            {primaryImageUrl &&
              (isScrollableImagesEnabled ? (
                <PlpTileScrollableImages
                  primaryImageUrl={primaryImageUrl}
                  secondaryImageUrl={secondaryImageUrl}
                  fetchAdditionalImages={fetchAdditionalImages}
                  productTitle={title}
                  productID={productId}
                  collectionHandle={collectionHandle}
                  plpTileIndex={index}
                  firstTileVisible={firstTileVisible}
                />
              ) : (
                <PlpTileHoverableImage
                  shouldRenderQuickAdd={shouldRenderQuickAdd}
                  primaryImageUrl={primaryImageUrl}
                  secondaryImageUrl={secondaryImageUrl}
                  altText={title}
                />
              ))}
          </a>
          {shouldRenderQuickAdd && extendedData?.productByHandle ? (
            <>
              {isDesktop ? (
                <QuickAddPanel
                  extendedData={extendedData}
                  price={price}
                  compareAtPrice={compareAtPrice}
                  soldOutCopy={soldOutCopy}
                  ga4options={ga4options}
                  ATBtype={
                    collectionHandle === 'wishlist'
                      ? 'Wishlist'
                      : collectionHandle === 'search-page-drawer'
                      ? 'Quick Add Search'
                      : 'Quick Add PLP'
                  }
                  purchaseLimitTagData={purchaseLimitTagData}
                />
              ) : (
                <QuickAddMobile
                  extendedData={extendedData}
                  price={price}
                  compareAtPrice={compareAtPrice}
                  ga4options={ga4options}
                  ATBtype={
                    collectionHandle === 'wishlist'
                      ? 'Wishlist'
                      : collectionHandle === 'search-page-drawer'
                      ? 'Quick Add Search'
                      : 'Quick Add PLP'
                  }
                  purchaseLimitTagData={purchaseLimitTagData}
                  quickFitSizeSelectorText={quickFitSizeSelectorText}
                />
              )}
            </>
          ) : null}
        </div>

        <div className={`info`}>
          {badgeCopy && <Badge badgeCopy={badgeCopy} isDiscountBadge={!!discountBadgeCopy} />}
          <div className={`product-name`}>
            <a
              href={href}
              onClick={() => {
                try {
                  fireGA4ClickOnProductFromListEvent(clickOnProductFromListGA4EventData);
                  if (isWishlist) {
                    heapTrack('Web - Click - Wishlist Product', {
                      isSignedIn: isCustomer,
                      productId,
                      'Wishlist Items Count': wishlistState.productIds.length,
                    });
                  }
                } catch (error) {
                  window.location.href = prependLocaleToPath(productURL);
                  console.log(error);
                }
              }}
            >
              <p className="body semibold">{baseTitle ? baseTitle : title}</p>
            </a>
            {colorName ? <p className="product-color body">{colorName}</p> : null}
          </div>
          {/* // ? [REVISIT] old css / styles */}
          <div className="card-price">
            {/* // ? [REVISIT] add sale price, once we fix the colorSwatch data */}
            <Fragment>
              <span
                className={`currency-formatting ${
                  compareAtPrice ? 'product-price red' : 'product-price regular__price'
                }`}
              >
                {productData.productType === 'Gift Card'
                  ? `${toLocalCurrencyShowEstimate(
                      parseFloat(productData?.priceRange?.minVariantPrice?.amount)
                    )} - ${toLocalCurrencyShowEstimate(parseFloat(productData?.priceRange?.maxVariantPrice?.amount))}`
                  : priceRangeString
                  ? priceRangeString
                  : toLocalCurrencyShowEstimate(price)}
              </span>
              {compareAtPrice && (
                <s style={{ marginLeft: 5 }} className="product-price regular__price">
                  {toLocalCurrencyShowEstimate(compareAtPrice)}
                </s>
              )}
            </Fragment>
          </div>
          {!hideSingleSwatch && availableColors.length >= 1 && (
            <div className="swatches-wrapper">
              {/* // ? [REVISIT] skeletal */}
              {dimensions ? (
                <Fragment>
                  <div className={`swatches`}>
                    {(swatchesWontFit ? availableColors.slice(0, swatchesThatWillFit - 1) : availableColors).map(
                      (data, idx) => {
                        const _isComingSoon = isComingSoon(data.tags);
                        return (
                          <SwatchUI
                            key={data.name}
                            colorData={data}
                            tooltipMessage={tooltipMessage}
                            isFirstSwatch={idx === 0}
                            _isComingSoon={_isComingSoon}
                            buttonProps={{
                              className: 'swatch',
                              'swatch-name': data.name,
                              active: idx === activeSwatchIndex,
                              onClick: () => {
                                generatedProductData.setActiveSwatchIndex(idx);
                                getExtendedProductData();
                                if (!discountBadgeCopy) {
                                  setProductBadgeCopy(productBadgeText(data.tags, data.totalInventory > 0));
                                }
                                pushEventToGTMDataLayer({
                                  event: 'alo_PLP_product_swatchSelection',
                                  swatchSwitchEventInformation: data,
                                });
                              },
                            }}
                          />
                        );
                      }
                    )}
                    {swatchesWontFit && (
                      <CustomPlusIcon
                        colorsWontFitCount={availableColors.length - swatchesThatWillFit + 1}
                        htmlProps={{
                          className: 'more-colors-icon',
                          href,
                          tabIndex: -1,
                          'aria-label': `available in ${availableColors.length} colors`,
                        }}
                      />
                    )}
                  </div>
                </Fragment>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {shouldRenderPLPBuilderContent && inlineContentEntry && (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <PLPBuilderContent contentEntryId={inlineContentEntry.content_entry_id} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
PlpTile = observer(PlpTile);
export { PlpTile };

// ? [REVISIT] at the very least limit mobile --> smaller sizes
const defaultMaxImageWidth = 320; // would it be worth to measure the img to compute width? + resizes?
export const getlargeImageWidth = (shopifyImageUrlWithMaxWidth_1x: string, maxWidth = defaultMaxImageWidth) => {
  // our current  PLP images don't take this into account, new nearly twice as big ... is that right? seems high?
  // disabled for now as requested; re add w/ Suspense
  const pixelRatio = window.devicePixelRatio;
  const largeImageWidth = Math.ceil(maxWidth * pixelRatio).toString();
  const largeImage = shopifyImageUrlWithMaxWidth_1x.replace(/_1x\./, `_${largeImageWidth}x.`);
  return largeImage;
};

export const handleImageFailure = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
  event.currentTarget.src = PRODUCT_TILE_FALLBACK_IMG;
};

export const getPricing = (extendedData: PlpProductsExtendedQuery | undefined, productData: ProductNode) => {
  const freshProductData = extendedData?.productByHandle;
  const minPrice = freshProductData
    ? Number(freshProductData?.priceRange.minVariantPrice.amount)
    : Number(productData.priceRange.minVariantPrice.amount);
  const maxPrice = freshProductData
    ? Number(freshProductData?.priceRange.maxVariantPrice.amount)
    : Number(productData.priceRange.maxVariantPrice.amount);
  const compareAtPrice = freshProductData
    ? Number(freshProductData?.compareAtPriceRange?.maxVariantPrice?.amount)
    : Number(productData.compareAtPriceRange?.maxVariantPrice?.amount);
  const priceRangeString =
    minPrice !== maxPrice && `${toLocalCurrencyShowEstimate(minPrice)} - ${toLocalCurrencyShowEstimate(maxPrice)}`;
  // Diff sale price. For US only?
  const tags = freshProductData ? freshProductData.tags : productData.tags;

  const diffSalePrice =
    window.differential_pricing_settings?.isDifferentialPricingEnabled && getOnlineSalePricing(tags, maxPrice);

  return {
    price: diffSalePrice && ShippingCountryState.countryCode?.toLowerCase() === 'us' ? diffSalePrice : minPrice,
    maxPrice,
    compareAtPrice: compareAtPrice > 0 ? compareAtPrice : null,
    priceRangeString,
  };
};
